import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CaseSensitive, ChevronRight, Replace, ReplaceAll, Search, WholeWord, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import { can } from "@ide/shared";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Tip } from "@/components/ui/overlay";
import { PanelHeader } from "@/components/ui/panel-header";
import { Spinner } from "@/components/ui/primitives";
import { get } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useColoredIcons } from "@/plugins/use-plugins";
import { activeEditor } from "./active-editor";
import { useRole, useTree, useWs } from "./context";
import { readFile, writeFile } from "./file-ops";
import { fileIcon } from "./file-tree";
import { useWorkspace } from "./store";

/** Texto pedido de fora (busca global da paleta): o painel Buscar começa com ele. */
export const useSearchSeed = create<{ q: string; at: number }>(() => ({ q: "", at: 0 }));

export function searchInProject(q: string) {
  useSearchSeed.setState({ q, at: Date.now() });
  useWorkspace.getState().setPanel("search");
}

interface SearchResult {
  results: Array<{ path: string; matches: Array<{ line: number; column: number; preview: string }> }>;
  total: number;
  truncated: boolean;
}

/** Abre o arquivo e leva o cursor até a linha (espera o editor do arquivo montar e carregar). */
export function openAt(fileId: string, line: number, needle?: string) {
  const ws = useWorkspace.getState();
  const before = ws.active === fileId ? null : activeEditor.current;
  ws.openFile(fileId);
  const started = Date.now();
  const tick = () => {
    const ed = activeEditor.current;
    if (ed && ed !== before && useWorkspace.getState().active === fileId && ed.view.state.doc.lines >= line) {
      ed.revealLine(line);
      if (needle) {
        const l = ed.view.state.doc.line(line);
        const col = l.text.toLowerCase().indexOf(needle.toLowerCase());
        if (col >= 0)
          ed.view.dispatch({ selection: { anchor: l.from + col, head: l.from + col + needle.length } });
      }
      ed.view.focus();
      return;
    }
    if (Date.now() - started < 4_000) setTimeout(tick, 60);
  };
  tick();
}

export interface MatchOptions {
  caseSensitive: boolean;
  wholeWord: boolean;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Expressão da busca (texto literal; Aa e palavra inteira como no VS Code). */
export function searchRegExp(needle: string, o: MatchOptions): RegExp {
  const body = escapeRe(needle);
  return new RegExp(o.wholeWord ? `(?<![\\w$])${body}(?![\\w$])` : body, o.caseSensitive ? "g" : "gi");
}

/** Troca todas as ocorrências no texto; devolve o novo texto e quantas trocou. */
export function replaceIn(text: string, needle: string, replacement: string, o: MatchOptions) {
  let count = 0;
  const next = text.replace(searchRegExp(needle, o), () => {
    count++;
    return replacement;
  });
  return { text: next, count };
}

function Highlight({
  text,
  needle,
  options,
  replacement,
}: {
  text: string;
  needle: string;
  options: MatchOptions;
  replacement: string | null;
}) {
  const m = needle ? searchRegExp(needle, options).exec(text) : null;
  if (!m) return <>{text}</>;
  const i = m.index;
  const hit = m[0];
  // Trecho longo: começa um pouco antes do termo, como no VS Code.
  const from = Math.max(0, i - 24);
  return (
    <>
      {from > 0 ? "…" : ""}
      {text.slice(from, i)}
      {replacement !== null ? (
        <>
          <del className="rounded-[2px] bg-[var(--danger)]/20 text-fg-muted">{hit}</del>
          <ins className="rounded-[2px] bg-accent-soft font-semibold text-accent no-underline">
            {replacement}
          </ins>
        </>
      ) : (
        <mark className="rounded-[2px] bg-fg/25 font-semibold text-fg">{hit}</mark>
      )}
      {text.slice(i + hit.length)}
    </>
  );
}

export function SearchPanel() {
  const { detail, session } = useWs();
  const role = useRole();
  const tree = useTree();
  const qc = useQueryClient();
  const colored = useColoredIcons();
  const [input, setInput] = useState(() => useSearchSeed.getState().q);
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [showReplace, setShowReplace] = useState(false);
  const [replacement, setReplacement] = useState("");
  const [options, setOptions] = useState<MatchOptions>({ caseSensitive: false, wholeWord: false });
  const [confirmAll, setConfirmAll] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const canWrite = can(role, "write");
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => ref.current?.focus(), []);
  useEffect(
    () =>
      useSearchSeed.subscribe((s) => {
        setInput(s.q);
        ref.current?.focus();
      }),
    [],
  );
  useEffect(() => {
    const t = setTimeout(() => setQ(input.trim()), 250);
    return () => clearTimeout(t);
  }, [input]);

  const { data, isFetching, error } = useQuery({
    queryKey: ["search", detail.project.id, q],
    queryFn: ({ signal }) =>
      get<SearchResult>(`/projects/${detail.project.id}/search?q=${encodeURIComponent(q)}`, signal),
    enabled: q.length > 0,
    staleTime: 5_000,
  });

  const idByPath = new Map([...tree.paths].map(([id, path]) => [path, id]));
  // O servidor busca sem diferenciar maiúsculas; Aa e palavra inteira filtram aqui.
  const results = useMemo(() => {
    if (!data || !q) return [];
    const re = searchRegExp(q, options);
    return data.results
      .map((r) => ({
        ...r,
        matches: r.matches.filter((m) => re.test(m.preview) && ((re.lastIndex = 0), true)),
      }))
      .filter((r) => r.matches.length);
  }, [data, q, options]);
  const total = results.reduce((n, r) => n + r.matches.length, 0);
  const files = results.length;
  const preview = showReplace && canWrite ? replacement : null;

  /** Substitui nos arquivos (edição normal: entra no histórico com a sua autoria e chega a todos). */
  const replaceFiles = async (paths: string[]) => {
    if (!q || !canWrite) return;
    setReplacing(true);
    let changed = 0;
    let occurrences = 0;
    try {
      for (const path of paths) {
        const text = await readFile(session, path);
        if (text === null) continue;
        const res = replaceIn(text, q, replacement, options);
        if (!res.count) continue;
        await writeFile(session, path, res.text);
        changed++;
        occurrences += res.count;
      }
      toast.success(
        occurrences
          ? `${occurrences} ${occurrences === 1 ? "ocorrência substituída" : "ocorrências substituídas"} em ${changed} ${changed === 1 ? "arquivo" : "arquivos"}`
          : "Nada para substituir",
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível substituir.");
    } finally {
      setReplacing(false);
      void qc.invalidateQueries({ queryKey: ["search", detail.project.id] });
    }
  };
  const toggle = (on: boolean) =>
    cn(
      "flex h-5 w-5 items-center justify-center rounded border text-fg-subtle transition-colors hover:text-fg",
      on ? "border-accent/60 bg-accent-soft text-accent" : "border-transparent",
    );

  return (
    <div className="flex h-full flex-col">
      <PanelHeader title="Buscar e substituir" />
      <div className="px-3 pb-2">
        <div className="flex items-start gap-1">
          {canWrite ? (
            <button
              type="button"
              onClick={() => setShowReplace((v) => !v)}
              aria-expanded={showReplace}
              aria-label={showReplace ? "Esconder substituir" : "Mostrar substituir"}
              title="Substituir"
              className="mt-1.5 flex h-5 w-4 shrink-0 items-center justify-center rounded text-fg-subtle hover:bg-bg-muted hover:text-fg"
            >
              <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", showReplace && "rotate-90")} />
            </button>
          ) : null}
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex h-8 items-center gap-1.5 rounded-lg border border-border bg-canvas px-2.5 focus-within:border-accent">
              <Search className="h-3.5 w-3.5 shrink-0 text-fg-subtle" />
              <input
                ref={ref}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && setInput("")}
                placeholder="Buscar em todos os arquivos"
                className="min-w-0 flex-1 bg-transparent text-[12.5px] outline-none placeholder:text-fg-subtle"
                aria-label="Buscar no projeto"
                spellCheck={false}
              />
              {isFetching ? <Spinner className="h-3 w-3" /> : null}
              {input ? (
                <button
                  onClick={() => setInput("")}
                  className="text-fg-subtle hover:text-fg"
                  aria-label="Limpar busca"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
              <Tip label="Diferenciar maiúsculas">
                <button
                  type="button"
                  aria-pressed={options.caseSensitive}
                  onClick={() => setOptions((o) => ({ ...o, caseSensitive: !o.caseSensitive }))}
                  className={toggle(options.caseSensitive)}
                  aria-label="Diferenciar maiúsculas"
                >
                  <CaseSensitive className="h-3.5 w-3.5" />
                </button>
              </Tip>
              <Tip label="Palavra inteira">
                <button
                  type="button"
                  aria-pressed={options.wholeWord}
                  onClick={() => setOptions((o) => ({ ...o, wholeWord: !o.wholeWord }))}
                  className={toggle(options.wholeWord)}
                  aria-label="Palavra inteira"
                >
                  <WholeWord className="h-3.5 w-3.5" />
                </button>
              </Tip>
            </div>
            {showReplace && canWrite ? (
              <div className="flex items-center gap-1">
                <div className="flex h-8 min-w-0 flex-1 items-center gap-1.5 rounded-lg border border-border bg-canvas px-2.5 focus-within:border-accent">
                  <Replace className="h-3.5 w-3.5 shrink-0 text-fg-subtle" />
                  <input
                    value={replacement}
                    onChange={(e) => setReplacement(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && e.altKey && total)
                        setConfirmAll(true);
                    }}
                    placeholder="Substituir por"
                    className="min-w-0 flex-1 bg-transparent text-[12.5px] outline-none placeholder:text-fg-subtle"
                    aria-label="Substituir por"
                    spellCheck={false}
                  />
                </div>
                <Tip label="Substituir tudo (Ctrl+Alt+Enter)">
                  <button
                    type="button"
                    disabled={!total || replacing}
                    onClick={() => setConfirmAll(true)}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg disabled:opacity-40"
                    aria-label="Substituir tudo"
                  >
                    {replacing ? <Spinner className="h-3.5 w-3.5" /> : <ReplaceAll className="h-3.5 w-3.5" />}
                  </button>
                </Tip>
              </div>
            ) : null}
          </div>
        </div>
        {q && data ? (
          <p className="mt-2 text-[11.5px] text-fg-subtle">
            {total === 0
              ? "Nenhum resultado."
              : `${total}${data.truncated ? "+" : ""} ${total === 1 ? "resultado" : "resultados"} em ${files} ${files === 1 ? "arquivo" : "arquivos"}`}
          </p>
        ) : null}
        {error ? (
          <p className="mt-2 text-[11.5px] text-[var(--code-error)]">Não foi possível buscar agora.</p>
        ) : null}
        {!q ? (
          <p className="mt-3 text-[11.5px] leading-relaxed text-fg-subtle">
            Busca o texto em todos os arquivos do projeto. Clique num resultado para abrir na linha; a seta à
            esquerda abre o substituir (num arquivo ou em todos). No arquivo aberto: Ctrl+F e Ctrl+H.
          </p>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto pb-4" role="tree" aria-label="Resultados da busca">
        {results.map((r) => {
          const id = idByPath.get(r.path);
          const open = !collapsed.has(r.path);
          const name = r.path.split("/").pop()!;
          const dir = r.path.slice(0, -name.length - 1);
          return (
            <div key={r.path} role="treeitem" aria-expanded={open}>
              <button
                onClick={() =>
                  setCollapsed((s) => {
                    const next = new Set(s);
                    if (next.has(r.path)) next.delete(r.path);
                    else next.add(r.path);
                    return next;
                  })
                }
                className="flex h-[22px] w-full items-center gap-1.5 pl-2 pr-3 text-left text-[13px] text-fg hover:bg-bg-muted/70"
              >
                <ChevronRight
                  className={cn(
                    "h-3.5 w-3.5 shrink-0 text-fg-subtle transition-transform",
                    open && "rotate-90",
                  )}
                />
                {colored ? (
                  <span className="shrink-0 text-fg-muted">{fileIcon(name, undefined, colored)}</span>
                ) : null}
                <span className="truncate">{name}</span>
                {dir ? <span className="truncate text-[11.5px] text-fg-subtle">{dir}</span> : null}
                <span className="ml-auto rounded-full bg-bg-emphasis px-1.5 font-mono text-[10px] text-fg-muted">
                  {r.matches.length}
                </span>
                {preview !== null ? (
                  <span
                    role="button"
                    tabIndex={0}
                    title="Substituir neste arquivo"
                    aria-label={`Substituir em ${name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      void replaceFiles([r.path]);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        void replaceFiles([r.path]);
                      }
                    }}
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-fg-subtle hover:bg-bg-emphasis hover:text-fg"
                  >
                    <ReplaceAll className="h-3 w-3" />
                  </span>
                ) : null}
              </button>
              {open
                ? r.matches.map((m) => (
                    <button
                      key={m.line}
                      disabled={!id}
                      onClick={() => id && openAt(id, m.line, q)}
                      className="flex h-[22px] w-full items-center gap-2 pl-[38px] pr-3 text-left font-mono text-[12px] text-fg-muted hover:bg-bg-muted/70 hover:text-fg disabled:opacity-50"
                      title={`${r.path}:${m.line}`}
                    >
                      <span className="min-w-0 flex-1 truncate whitespace-pre">
                        <Highlight text={m.preview} needle={q} options={options} replacement={preview} />
                      </span>
                      <span className="shrink-0 text-[10.5px] text-fg-subtle">{m.line}</span>
                    </button>
                  ))
                : null}
            </div>
          );
        })}
      </div>
      <ConfirmDialog
        open={confirmAll}
        onOpenChange={setConfirmAll}
        title={`Substituir ${total} ${total === 1 ? "ocorrência" : "ocorrências"}?`}
        description={`"${q}" vira "${replacement}" em ${files} ${files === 1 ? "arquivo" : "arquivos"}. Entra no histórico com a sua autoria; dá para voltar pelo Histórico de alterações.`}
        actionLabel="Substituir tudo"
        onConfirm={() => {
          setConfirmAll(false);
          void replaceFiles(results.map((r) => r.path));
        }}
      />
    </div>
  );
}
